import { type HttpErrorResponse } from '@angular/common/http';
import type { ThemeSettingsState } from '@sites/site-theme/src/state';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { serializeThemeSettings } from '@sites/site-theme';
import { type SiteCreateRequest, type SiteEntity, type ShareBlocks, ContentSourceType } from '@common/types/site.type';
import type { SitePage, SitePageEntity } from '@common/types/site-page.type';
import type { Template } from '@common/types/template.type';
import type { DenormalizedBlockEntity } from '@web/types';

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, exhaustMap, forkJoin, fromEvent, mergeMap, of, timer } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

import { extendWebFileDataPromise, isUuid, preparedImageData, stringifyPageStructure } from '@common/helpers';
import { buildFileSrcSets } from '@components/sp-file-manager/src/helpers';
import { EContentSources, WidgetDataSubTypes, WidgetsDataTypes } from '@common/enums';

import { environment } from '@web-env/environment';

import { FileManagerHttpService } from '@web/core/services/REST/file-manager.http.service';

import { SiteService } from '@web/store/website/site/site.service';
import { PagesService } from '@web/store/website/pages/pages.service';
import { ContentService } from '@web/store/website/content/content.service';
import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';
import { restoreUnmigratedBlocks } from '@web/store/page-structure/page-structure/unmigrated-blocks';
import { sharesThemeWithMiniLanding, themeToPreserve } from '@web/store/website/site/stored-theme';

import { SiteHttpService } from '@web/store/website/site/site.http.service';
import { TemplatesHttpService } from '@web/core/services/REST/tempates.http.service';
import { PagesHttpService } from '@web/store/website/pages/pages.http.service';
import { DomainsHttpService } from '@web/store/website/domains/domains.http.service';
import { SettingsHttpService } from '@web/store/website/settings/settings.http.service';

import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { WebBlockFormService } from '@web/core/services/web-block-form.service';
import { WebColumnFormService } from '@web/core/services/web-column-form.service';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';
import type { SelectedFileExtended } from '@common/types/file.type';

import AppActions from '@web/store/app/app.actions';
import LayoutActions from '@web/store/layout/layout.actions';
import ContentActions from '@web/store/website/content/content.actions';
import NotifyAction from '@common/utils/notify/notify.actions';
import PagesActions from '@web/store/website/pages/pages.actions';
import DomainsActions from '@web/store/website/domains/domains.actions';
import SettingsActions from '@web/store/website/settings/settings.actions';
import SiteActions from './site.actions';
import { UserFacade } from '@web/store/user/user.facade';
import { TranslateService } from '@ngx-translate/core';

/** How long to wait before trying a save again when the browser never noticed the connection go. */
const SAVE_RETRY_DELAY = 10_000;

@Injectable()
export class SiteEffects {
    private readonly actions$ = inject(Actions);
    private readonly websiteHttpService = inject(SiteHttpService);
    private readonly pagesHttpService = inject(PagesHttpService);
    private readonly domainsHttpService = inject(DomainsHttpService);
    private readonly settingsHttpService = inject(SettingsHttpService);
    private readonly templateHttpService = inject(TemplatesHttpService);
    private readonly fileManageHttpService = inject(FileManagerHttpService);
    private readonly appFacade = inject(AppFacade);
    private readonly themeState = inject(ThemeStateService);
    private readonly routerFacade = inject(RouterFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly contentFacade = inject(ContentFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly siteService = inject(SiteService);
    private readonly pagesService = inject(PagesService);
    private readonly settingsFacade = inject(SettingsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly contentService = inject(ContentService);
    private readonly blockFormService = inject(WebBlockFormService);
    private readonly columnFormService = inject(WebColumnFormService);
    private readonly pageStructureService = inject(PageStructureService);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);
    private readonly domainsFacade = inject(DomainsFacade);
    private readonly userFacade = inject(UserFacade);
    private readonly translateService = inject(TranslateService);

    /** Told once per run of failed saves, not once per retry. */
    private isSaveOfflineNotified = false;

    public readonly loadSiteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.loadSiteSuccess),
            withLatestFrom(this.routerFacade.routerQueryParams$, ({ site, currentPage }, routerQueryParams) => ({
                site,
                currentPage,
                routerQueryParams,
            })),
            map(({ site, currentPage, routerQueryParams }) => {
                if (routerQueryParams.templateId) {
                    return SiteActions.navigateTemplateSitePage({
                        templateId: routerQueryParams.templateId,
                        siteId: site.id.toString(),
                        pageId: currentPage.id.toString(),
                    });
                }

                return PagesActions.navigateSitePage({
                    siteId: site.id.toString(),
                    pageId: currentPage.id.toString(),
                    skipContentChange: true,
                });
            })
        )
    );

    public readonly loadTemplateSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteActions.loadTemplateSuccess),
                map(({ template }) => this.contentService.setInitialContent(template.contents, template.contents))
            ),
        { dispatch: false }
    );

    public readonly saveSiteRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.saveSiteRequest),
            concatLatestFrom(() => [
                this.routerFacade.routerParams$,
                this.routerFacade.routerQueryParams$,
                this.appFacade.isAdmin$,
            ]),
            map(
                ([
                    { exit, publish, navigateTo, skipContentChange, isRedirectTabClose, capturedPageId },
                    routerParams,
                    routerQueryParams,
                    isAdmin,
                ]) => {
                    if (this.siteService.isNewSite(routerParams, isAdmin)) {
                        // TODO: pass capturedSiteId and provide check without a routerParams here
                        return SiteActions.createSiteRequest({
                            exit,
                            publish,
                            navigateTo,
                            templateId: routerQueryParams.templateId ?? 0,
                            isRedirectTabClose,
                        });
                    }

                    if (this.siteService.isNewPage(routerParams, isAdmin)) {
                        return PagesActions.createPageRequest({
                            exit,
                            publish,
                            navigateTo,
                            skipContentChange,
                            templateId: routerQueryParams.templateId ?? 0,
                            isRedirectTabClose,
                        });
                    }

                    if (isAdmin) {
                        return SiteActions.updateAdminSiteTemplateRequest({ exit });
                    }

                    return PagesActions.updatePageRequest({
                        exit,
                        publish,
                        navigateTo,
                        skipContentChange,
                        isRedirectTabClose,
                        capturedPageId,
                    });
                }
            )
        )
    );

    public readonly createSiteRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.createSiteRequest),
            concatLatestFrom(() => [
                this.contentFacade.sharedBlocks$,
                this.blocksFacade.structure$,
                this.contentFacade.unmigratedBlocks$,
                this.contentFacade.siteContent$,
            ]),
            map(
                ([
                    { publish, navigateTo, exit, templateId, isRedirectTabClose },
                    sharedBlocks,
                    structure,
                    unmigratedBlocks,
                    siteContent,
                ]) => ({
                    publish,
                    navigateTo,
                    exit,
                    structure,
                    unmigratedBlocks,
                    themeSettings: this.themeState.settings(),
                    storedTheme: themeToPreserve(siteContent, this.themeState.edited()),
                    sharedBlocks,
                    templateId,
                    isRedirectTabClose,
                })
            ),
            switchMap(
                ({
                    navigateTo,
                    publish,
                    exit,
                    structure,
                    unmigratedBlocks,
                    themeSettings,
                    storedTheme,
                    sharedBlocks,
                    templateId,
                    isRedirectTabClose,
                }) => {
                    const { preparedStructure, preparedSharedBlocks } = this.pageStructureService.prepareStructure(
                        structure,
                        sharedBlocks,
                        unmigratedBlocks
                    );
                    const sitePayload = this.siteService.formatSaveData({
                        structure: preparedStructure,
                        themeSettings,
                        storedTheme,
                        sharedBlocks: preparedSharedBlocks,
                        templateId,
                        publish,
                    });
                    return this.websiteHttpService.createSite(sitePayload).pipe(
                        mergeMap((site) =>
                            forkJoin({
                                data: this.pagesHttpService.getSitePages(site.id),
                                domains: this.domainsHttpService.getSiteDomains(site.id),
                                settings: this.settingsHttpService.getSiteSettings(site.id),
                                site: of(site),
                            })
                        ),
                        switchMap(({ data, site, domains, settings }) => [
                            // The editor holds this page's content: nobody could have written it since.
                            ...(data.pages[0]?.version
                                ? [
                                      ContentActions.setPageVersion({
                                          pageId: data.pages[0].id,
                                          version: data.pages[0].version,
                                      }),
                                  ]
                                : []),
                            SiteActions.createSiteSuccess({ site }),
                            PagesActions.loadPagesSuccess({ pages: data.pages, total: data.total }),
                            DomainsActions.loadDomainsSuccess({ domains }),
                            SettingsActions.loadSiteSettingsSuccess({ settings }),
                            ContentActions.updateSiteSharedBlocksContent({ sharedBlocks: preparedSharedBlocks }),
                            ...this.siteService.handleSaveSiteSuccess({
                                site,
                                page: data.pages[0],
                                publish,
                                exit,
                                navigateTo,
                                isAdmin: false,
                                isRedirectTabClose,
                            }),
                        ]),
                        catchError(({ error }) => this.siteService.handleSaveSiteError(error))
                    );
                }
            )
        )
    );

    public readonly createPageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.createPageRequest),
            concatLatestFrom(() => [
                this.siteFacade.site$,
                this.contentFacade.sharedBlocks$,
                this.blocksFacade.structure$,
                this.routerFacade.routerParams$,
                this.pagesFacade.pages$,
                this.appFacade.isEcommerceMode$,
                this.contentFacade.unmigratedBlocks$,
                this.contentFacade.siteContent$,
            ]),
            map(
                ([
                    { navigateTo, publish, exit, templateId, isRedirectTabClose },
                    site,
                    sharedBlocks,
                    structure,
                    routerParams,
                    pages,
                    isEcommerceMode,
                    unmigratedBlocks,
                    siteContent,
                ]) => ({
                    navigateTo,
                    publish,
                    exit,
                    site,
                    structure,
                    unmigratedBlocks,
                    themeSettings: this.themeState.settings(),
                    storedTheme: themeToPreserve(
                        siteContent,
                        this.themeState.edited(),
                        sharesThemeWithMiniLanding(pages)
                    ),
                    routerParams,
                    sharedBlocks,
                    page: this.pagesService.findCurrentPage(pages, routerParams),
                    templateId,
                    isRedirectTabClose,
                    isEcommerceMode,
                })
            ),
            switchMap(
                ({
                    navigateTo,
                    publish,
                    exit,
                    site,
                    page,
                    structure,
                    unmigratedBlocks,
                    themeSettings,
                    storedTheme,
                    routerParams,
                    sharedBlocks,
                    templateId,
                    isRedirectTabClose,
                    isEcommerceMode,
                }) => {
                    const { preparedStructure, preparedSharedBlocks } = this.pageStructureService.prepareStructure(
                        structure,
                        sharedBlocks,
                        unmigratedBlocks
                    );
                    const pagePayload = this.siteService.formatCreatePageData({
                        structure: preparedStructure,
                        themeSettings,
                        storedTheme,
                        sharedBlocks: preparedSharedBlocks,
                        page,
                        templateId,
                        publish,
                        contentSource: isEcommerceMode ? ContentSourceType.Ecommerce : ContentSourceType.Website,
                    });

                    return this.pagesHttpService.createPage(site.id, pagePayload).pipe(
                        switchMap((createdPage: SitePage) => [
                            ...(createdPage.version
                                ? [
                                      ContentActions.setPageVersion({
                                          pageId: createdPage.id,
                                          version: createdPage.version,
                                      }),
                                  ]
                                : []),
                            PagesActions.createPageSuccess({
                                page: this.pagesService.mapPageToEntityPage(createdPage),
                                oldPageId: routerParams.pageId,
                            }),
                            ContentActions.updateSiteSharedBlocksContent({ sharedBlocks: preparedSharedBlocks }),
                            ...this.siteService.handleSaveSiteSuccess({
                                site,
                                page: this.pagesService.mapPageToEntityPage(createdPage),
                                publish,
                                exit,
                                navigateTo,
                                isAdmin: false,
                                resetContent: isUuid(routerParams.pageId),
                                isRedirectTabClose,
                            }),
                        ]),
                        catchError(({ error }) => this.siteService.handleSaveSiteError(error))
                    );
                }
            )
        )
    );

    public readonly updatePageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.updatePageRequest),
            concatLatestFrom(() => [
                this.siteFacade.site$,
                this.pagesFacade.pages$,
                this.contentFacade.sharedBlocks$,
                this.siteFacade.newSiteData$,
                this.blocksFacade.structure$,
                this.routerFacade.routerParams$,
                this.contentFacade.isReset$,
                this.contentFacade.unmigratedBlocks$,
                this.contentFacade.siteContent$,
            ]),
            map(
                ([
                    { navigateTo, publish, skipContentChange, exit, isRedirectTabClose, capturedPageId },
                    siteData,
                    pages,
                    sharedBlocks,
                    newSiteData,
                    structure,
                    routerParams,
                    isReset,
                    unmigratedBlocks,
                    siteContent,
                ]) => ({
                    navigateTo,
                    siteData,
                    pages,
                    structure,
                    unmigratedBlocks,
                    themeSettings: this.themeState.settings(),
                    storedTheme: themeToPreserve(
                        siteContent,
                        this.themeState.edited(),
                        sharesThemeWithMiniLanding(pages)
                    ),
                    sharedBlocks,
                    publish,
                    exit,
                    skipContentChange,
                    newSiteData,
                    isRedirectTabClose,
                    capturedPageId,
                    routerParams,
                    isReset,
                })
            ),
            // In order, not `switchMap`: a save cancelled in the browser is still applied by the server,
            // which rotates the page's version, so the save that cancelled it would carry a stale one.
            concatMap(
                ({
                    navigateTo,
                    siteData,
                    pages,
                    structure,
                    unmigratedBlocks,
                    themeSettings,
                    storedTheme,
                    sharedBlocks,
                    publish,
                    exit,
                    newSiteData,
                    skipContentChange,
                    isRedirectTabClose,
                    capturedPageId,
                    routerParams,
                    isReset,
                }) => {
                    const { preparedStructure, preparedSharedBlocks } = this.pageStructureService.prepareStructure(
                        structure,
                        sharedBlocks,
                        unmigratedBlocks
                    );

                    // Read when the request goes out rather than with the action, so a save queued behind
                    // another one sends the version that one came back with.
                    const request$ = this.contentFacade.pageVersions$.pipe(
                        take(1),
                        switchMap((versions) =>
                            isReset
                                ? this.pagesHttpService.resetSiteToPublished(routerParams.siteId, routerParams.pageId)
                                : this.pagesHttpService.updatePage(
                                      siteData.id,
                                      capturedPageId,
                                      this.siteService.formatUpdateData(
                                          newSiteData,
                                          this.pagesService.findCurrentPage(pages, { pageId: capturedPageId }),
                                          preparedStructure,
                                          themeSettings,
                                          preparedSharedBlocks,
                                          publish,
                                          versions[String(capturedPageId)],
                                          storedTheme
                                      )
                                  )
                        )
                    );

                    const save$ = request$.pipe(
                        map((page: SitePageEntity) => ({ site: siteData, page })),
                        switchMap(({ site, page }) => [
                            ...(page.version
                                ? [ContentActions.setPageVersion({ pageId: page.id, version: page.version })]
                                : []),
                            PagesActions.updatePageSuccess({ page: this.pagesService.mapPageToEntityPage(page) }),
                            ContentActions.updatePageStructureContent({ structure: stringifyPageStructure(structure) }),
                            ContentActions.updateSiteThemeSettingsContent({
                                themeSettings: storedTheme?.settings ?? serializeThemeSettings(themeSettings),
                            }),
                            ContentActions.updateSiteSharedBlocksContent({ sharedBlocks: preparedSharedBlocks }),
                            ContentActions.setIsReset({ isReset: false }),
                            ...this.siteService.handleSaveSiteSuccess({
                                site,
                                page: this.pagesService.mapPageToEntityPage(page),
                                publish,
                                navigateTo,
                                exit,
                                skipContentChange,
                                isAdmin: false,
                                isRedirectTabClose,
                            }),
                        ]),
                        catchError(({ error, status }: HttpErrorResponse) =>
                            this.siteService.handleSaveSiteError(error, status, {
                                siteId: siteData.id,
                                pageId: capturedPageId,
                            })
                        )
                    );

                    return combineLatest([
                        this.contentFacade.staleVersionPageIds$,
                        this.contentFacade.deletedPageIds$,
                    ]).pipe(
                        take(1),
                        switchMap(([stale, deleted]) => {
                            if (deleted.includes(String(capturedPageId))) {
                                return this.refuseSaveToDeletedPage(String(siteData.id), {
                                    navigateTo,
                                    skipContentChange,
                                    exit,
                                    isRedirectTabClose,
                                });
                            }

                            // Refused while the page reloads after a 409: it would carry the version just
                            // refused, and its edits were made on the page the server has since replaced.
                            return !isReset && stale.includes(String(capturedPageId))
                                ? [
                                      SiteActions.saveSiteFailure({ error: null }),
                                      ContentActions.setIsDirty({ isDirty: false }),
                                      ...(exit ? [AppActions.exit({ isRedirectTabClose })] : []),
                                  ]
                                : save$;
                        })
                    );
                }
            )
        )
    );

    // A save that never reached the server is sent again: as soon as the browser is back online, or —
    // when it never noticed the connection go (the server out of reach) — after a pause.
    public readonly retrySaveOffline$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.saveSiteOffline),
            switchMap(() => {
                const retry$: Observable<unknown> = navigator.onLine
                    ? timer(SAVE_RETRY_DELAY)
                    : fromEvent(window, 'online').pipe(take(1));

                // Saved meanwhile, by the next edit.
                return retry$.pipe(takeUntil(this.actions$.pipe(ofType(SiteActions.saveSiteSuccess))));
            }),
            map(() => ContentActions.triggerSaveContent())
        )
    );

    public readonly notifySaveOffline$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.saveSiteOffline, SiteActions.saveSiteSuccess),
            filter(({ type }) => {
                const shouldNotify = type === SiteActions.saveSiteOffline.type && !this.isSaveOfflineNotified;
                this.isSaveOfflineNotified = type === SiteActions.saveSiteOffline.type;

                return shouldNotify;
            }),
            map(() =>
                NotifyAction.showNotify({
                    toastType: 'error',
                    message: this.translateService.instant('page_save_offline'),
                    // Longer than the usual toast: it says an edit is not stored yet.
                    config: { timeOut: 8000 },
                })
            )
        )
    );

    public readonly updateAdminSiteTemplateRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.updateAdminSiteTemplateRequest),
            concatLatestFrom(() => [
                this.siteFacade.site$,
                this.blocksFacade.structure$,
                this.contentFacade.unmigratedBlocks$,
                this.contentFacade.siteContent$,
            ]),
            map(([, siteData, structure, unmigratedBlocks, siteContent]) => ({
                siteData,
                structure: restoreUnmigratedBlocks(structure, unmigratedBlocks),
                themeSettings: this.themeState.settings(),
                storedTheme: themeToPreserve(siteContent, this.themeState.edited()),
            })),
            switchMap(({ siteData, structure, themeSettings, storedTheme }) =>
                this.templateHttpService
                    .updateAdminTemplate(
                        this.siteService.formatUpdateAdminTemplate(
                            siteData as any,
                            structure,
                            themeSettings,
                            storedTheme
                        ),
                        siteData.id
                    )
                    .pipe(
                        map((template: Template) => ({ site: template, page: null })),
                        switchMap(({ site, page }) =>
                            this.siteService.handleSaveSiteSuccess({
                                site: site as any,
                                page,
                                // exit,
                                isAdmin: true,
                            })
                        ),
                        catchError(({ error }) => this.siteService.handleSaveSiteError(error))
                    )
            )
        )
    );

    public readonly previewSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LayoutActions.showPreview),
            concatLatestFrom(() => [
                this.blocksFacade.structure$,
                this.settingsFacade.showPoweredBy$,
                this.contentFacade.sharedBlocks$,
                this.routerFacade.routerParams$,
                this.appFacade.locale$,
                this.shopCartIconFacade.cartIconData$,
                this.shopCartIconFacade.isCartEnabled$,
                this.domainsFacade.siteDomain$,
                this.userFacade.userId$,
                this.contentFacade.unmigratedBlocks$,
                this.userFacade.memberAreaUserCreatedAt$,
            ]),
            map(
                ([
                    ,
                    structure,
                    showPoweredBy,
                    sharedBlocks,
                    params,
                    lang,
                    cartIconData,
                    isCartEnabled,
                    domain,
                    userId,
                    unmigratedBlocks,
                    userCreatedAt,
                ]) => ({
                    structure: restoreUnmigratedBlocks(structure, unmigratedBlocks),
                    themeSettings: this.themeState.settings(),
                    showPoweredBy,
                    sharedBlocks,
                    params,
                    lang,
                    cartIconData,
                    isCartEnabled,
                    domain,
                    userId,
                    userCreatedAt,
                })
            ),
            switchMap(
                ({
                    structure,
                    themeSettings,
                    showPoweredBy,
                    params,
                    sharedBlocks,
                    lang,
                    cartIconData,
                    isCartEnabled,
                    domain,
                    userId,
                    userCreatedAt,
                }) =>
                    this.websiteHttpService
                        .getSitePreview({
                            ...this.siteService.formatPreviewData({
                                structure,
                                siteId: params.siteId,
                                pageId: params.pageId,
                                sharedBlocks,
                                themeSettings,
                                cartIconData,
                                isCartEnabled,
                                showPoweredBy,
                                domain,
                                contentSourceId: EContentSources.Website,
                                isEnabledAnimations: true,
                                userId,
                                userCreatedAt,
                            }),
                            lang,
                        })
                        .pipe(map(({ content }) => SiteActions.previewSiteSuccess({ content })))
            )
        )
    );

    public readonly uploadImage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteActions.uploadImage),
                map((uploadData) => ({ ...uploadData.data })),
                switchMap((data) => {
                    const { image, folder = '/' } = data;
                    return forkJoin({
                        images: this.fileManageHttpService
                            .loadFile({ file: image, folder: folder })
                            .pipe(
                                catchError(({ message }: HttpErrorResponse) => [
                                    SiteActions.uploadImageFailure({ error: message }),
                                ])
                            ),
                        data: of(data),
                    });
                }),
                map(({ images, data }) => {
                    const {
                        widgetId,
                        widgetImage,
                        dataType,
                        dataSubType,
                        index,
                        entityId,
                        entityImage,
                        device = 'desktop',
                        isSync,
                        userHash,
                        folder = '/',
                        needResize = true,
                    } = data;

                    const editedImage = images[0];
                    const preparedImgFile =
                        dataType === WidgetsDataTypes.backgroundImage
                            ? preparedImageData(entityImage, editedImage, userHash, folder)
                            : preparedImageData(widgetImage, editedImage, userHash, folder);

                    extendWebFileDataPromise(preparedImgFile, environment.fileManagerCdnUrl, needResize).then(
                        (extendedFile) => {
                            this.siteFacade.uploadImageSuccess();
                            let srcSets: SelectedFileExtended['srcSets'] = [];
                            if (needResize) {
                                srcSets = buildFileSrcSets(extendedFile, environment.fileManagerCdnUrl);

                                this.siteFacade.resizeImage({
                                    path: extendedFile.path,
                                    url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
                                });
                            }
                            const data = {};
                            switch (dataType) {
                                case WidgetsDataTypes.backgroundImage:
                                    this.writeBackgroundImage(
                                        dataSubType,
                                        entityId,
                                        { ...extendedFile, ...{ srcSets } },
                                        device,
                                        isSync
                                    );
                                    return;
                                case WidgetsDataTypes.images:
                                    this.widgetsFacade.updateGalleryImageData(
                                        widgetId,
                                        { ...extendedFile, ...{ srcSets } },
                                        index
                                    );
                                    return;

                                case WidgetsDataTypes.customPreviewFile: {
                                    //video
                                    const previewUrl = `${environment.fileManagerCdnUrl}${extendedFile.url}`;
                                    data[dataType] = { ...extendedFile, ...{ srcSets } };
                                    data['customPreviewUrl'] = previewUrl;
                                    this.widgetsFacade.updateWidgetData(widgetId, data);
                                    return;
                                }
                                case WidgetsDataTypes.uploadImage:
                                    data[dataType] = { ...extendedFile, ...{ srcSets } };
                                    data['logoWidth'] = extendedFile.imageWidth;
                                    this.widgetsFacade.updateWidgetData(widgetId, data);
                                    return;
                                case WidgetsDataTypes.product:
                                    this.widgetsFacade.updateProductImageData(widgetId, {
                                        ...extendedFile,
                                        ...{ srcSets },
                                    });
                                    return;
                                default:
                                    data[dataType] = { ...extendedFile, ...{ srcSets } };
                                    this.widgetsFacade.updateWidgetData(widgetId, data);
                                    this.widgetsFacade.updateWidgetStyles(widgetId, {
                                        maxWidth: extendedFile.imageWidth,
                                    });
                            }
                        }
                    );
                })
            ),
        { dispatch: false }
    );

    public readonly onlyUploadImage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteActions.uploadImage),
                map((uploadData) => ({ ...uploadData.data })),
                switchMap((data) => {
                    const { image, folder = '/' } = data;

                    return this.fileManageHttpService.loadFile({ file: image, folder }).pipe(
                        map(() => SiteActions.uploadImageSuccess()),
                        catchError((error: HttpErrorResponse) =>
                            of(SiteActions.uploadImageFailure({ error: error.message }))
                        )
                    );
                })
            ),
        { dispatch: false }
    );

    public readonly resizeImage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteActions.resizeImage),
                filter(({ path }) => {
                    const pathToLowerCase = path.toLowerCase();

                    return !pathToLowerCase.endsWith('gif') && !pathToLowerCase.endsWith('svg');
                }),
                mergeMap(({ path, url }) =>
                    this.fileManageHttpService.resizeFile(url, path).pipe(catchError(() => of(null)))
                )
            ),
        { dispatch: false }
    );

    public readonly saveSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.saveSite),
            withLatestFrom(
                this.siteFacade.isSaving$,
                this.contentFacade.isDirty$,
                ({ publish, exit, navigateTo, skipContentChange, capturedPageId }, isSaving, isDirty) => ({
                    publish,
                    exit,
                    navigateTo,
                    skipContentChange,
                    isSaving,
                    isDirty,
                    capturedPageId,
                })
            ),
            filter(({ isSaving, isDirty }) => !isSaving && isDirty),
            switchMap(({ publish, exit, navigateTo, skipContentChange, capturedPageId }) => {
                return [SiteActions.saveSiteRequest({ exit, publish, navigateTo, skipContentChange, capturedPageId })];
            })
        )
    );

    public readonly updateSite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteActions.updateSiteRequest),
            exhaustMap(({ siteId, data, updateDomains }) =>
                forkJoin({
                    site: this.websiteHttpService.updateSite(siteId, data),
                    updateDomains: of(updateDomains),
                })
            ),
            map(({ site, updateDomains }) => {
                if (updateDomains) {
                    this.domainsFacade.loadDomains(site.id.toString());
                }
                return SiteActions.updateSiteSuccess({ site });
            })
        )
    );

    /**
     * There is nothing left to save to, so the edit is dropped — with a word, not as a quiet "saved". A
     * page switch or an exit riding on the save still happens: refusing those too would keep the user on
     * a page that no longer exists.
     */
    private refuseSaveToDeletedPage(
        siteId: string,
        {
            navigateTo,
            skipContentChange,
            exit,
            isRedirectTabClose,
        }: {
            navigateTo?: { pageId?: string; siteId?: string; isNew?: boolean };
            skipContentChange?: boolean;
            exit?: boolean;
            isRedirectTabClose?: boolean;
        }
    ): Action[] {
        return [
            ...(navigateTo || exit
                ? []
                : [
                      NotifyAction.showNotify({
                          toastType: 'warning',
                          message: this.translateService.instant('page_deleted_elsewhere'),
                      }),
                  ]),
            SiteActions.saveSiteFailure({ error: null }),
            ContentActions.setIsDirty({ isDirty: false }),
            ...(navigateTo?.isNew ? [PagesActions.createNewPage()] : []),
            ...(navigateTo?.pageId
                ? [
                      PagesActions.navigateSitePage({
                          siteId: navigateTo.siteId || siteId,
                          pageId: navigateTo.pageId,
                          skipContentChange,
                      }),
                  ]
                : []),
            ...(exit ? [AppActions.exit({ isRedirectTabClose })] : []),
        ];
    }

    /**
     * Lands a freshly uploaded background image on the block or the column that
     * asked for it — through that entity's signal form, not the store.
     *
     * The form is the single writer for an entity's props: a facade dispatch here
     * left the open settings panel's form holding the previous image, so its next
     * edit wrote that stale value back over this one. `isSync` writes the pick to
     * every breakpoint, which is what the panel's synchronise switch means.
     */
    private writeBackgroundImage(
        dataSubType: WidgetDataSubTypes | undefined,
        entityId: string,
        file: SelectedFileExtended,
        device: 'desktop' | 'tablet' | 'mobile',
        isSync?: boolean
    ): void {
        const devices = isSync ? (['desktop', 'tablet', 'mobile'] as const) : [device];

        if (dataSubType === WidgetDataSubTypes.block) {
            this.blockFormService.writeThrough(entityId, (form) =>
                form.transact(() => devices.forEach((target) => form.setBackgroundImage(file as never, target)))
            );
            return;
        }

        if (dataSubType === WidgetDataSubTypes.column) {
            this.columnFormService.writeThrough(entityId, (form) => {
                if (!('setBackgroundImage' in form)) {
                    return;
                }

                form.transact(() => devices.forEach((target) => form.setBackgroundImage(file as never, target)));
            });
        }
    }
}
